import { useEffect } from 'react';
import { analytics } from 'libs';
import Filter from './Filter';
import Toggle from './Toggle';

interface InsuranceFilters {
  insurance?: string;
  insurable?: string;
}

const INSURANCE_TO_FILTERS_MAP: Record<string, InsuranceFilters> = {
  'Conventional Insurable': {
    insurance: 'Conventional',
    insurable: 'Yes',
  },
  'Conventional Uninsurable': {
    insurance: 'Conventional',
    insurable: 'No',
  },
  Insured: {
    insurance: 'Insured',
    insurable: undefined,
  },
};

const FILTERS_NO_SELECTION: InsuranceFilters = {
  insurance: undefined,
  insurable: undefined,
};

const insuranceOptions = [
  { key: 'Conventional Insurable', value: 'Conventional Insurable' },
  { key: 'Conventional Uninsurable', value: 'Conventional Uninsurable' },
  { key: 'Insured', value: 'Insured' },
];

const getInsuranceOption = ({ insurance, insurable }: InsuranceFilters) => {
  const entry =
    Object.entries(INSURANCE_TO_FILTERS_MAP).find(
      ([, filters]) => filters.insurance === insurance && filters.insurable === insurable
    ) ?? [];
  const [key] = entry;
  return insuranceOptions.find((option) => option.key === key) ?? null;
};

const getInsuranceFilters = (optionKey: string): InsuranceFilters => {
  return INSURANCE_TO_FILTERS_MAP[optionKey] ?? FILTERS_NO_SELECTION;
};

/**
 * Clear invalid insurance filters hook
 *
 * It's possible for filters to contain an invalid combination of insurance values,
 * e.g.,
 *
 * ```
 * {
 *   insurance: 'Insured',
 *   insurable: 'No',
 * }
 * ```
 *
 * NOTE: If there are multiple potential invalid filters, it might be better to
 * handle them in a single location. That way the app will make at most a single
 * extra request when there's one or more invalid filters
 */
const useClearInvalidInsuranceFilters = ({
  filters,
  onFilter,
  option,
}: {
  filters: InsuranceFilters;
  onFilter: (update: InsuranceFilters) => void;
  option: { key: string; value: string } | null;
}) => {
  useEffect(() => {
    const hasInsuranceFilter = Boolean(filters.insurance || filters.insurable);
    if (!hasInsuranceFilter || option) return;

    onFilter(FILTERS_NO_SELECTION);
  }, [filters, option, onFilter]);
};

interface InsuranceFilterProps {
  filters: InsuranceFilters;
  isPro?: boolean;
  onFilter: (update: InsuranceFilters) => void;
}

const InsuranceFilter = ({ filters, isPro = false, onFilter }: InsuranceFilterProps) => {
  const option = getInsuranceOption(filters);
  const currKey = option ? option.key : false;

  useClearInvalidInsuranceFilters({ filters, onFilter, option });

  const onChange = (key: string) => {
    const isDeselection = key === currKey;
    const newFilters = isDeselection ? FILTERS_NO_SELECTION : getInsuranceFilters(key);

    onFilter(newFilters);

    analytics.productSearch.filter({
      insurance: isDeselection ? 'cleared' : key,
    });
  };

  return (
    <Filter name="Insurance" isPro={isPro}>
      <Toggle
        isPro={isPro}
        onChange={onChange}
        options={insuranceOptions}
        testId="insurance-filter"
        value={currKey}
        variant="vertical"
      />
    </Filter>
  );
};

export default InsuranceFilter;
